import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  X, ExternalLink, Share2, Copy, Check,
  Music2, ShoppingCart, Apple, Play, Pause,
  Youtube, Headphones, ArrowUpRight, Tag, Zap, MessageSquare, Disc3
} from "lucide-react";
import { formatDate, getSpotifyEmbedUrl, applyAffiliateLink, nameToSlug } from "../utils/affiliateHelper";
import { CoverImage } from "./ReleaseCard";
import ArtistLinks from "./ArtistLinks";
import VoteButton from "./VoteButton";
import HypeButton from "./HypeButton";
import FavoriteButton from "./FavoriteButton";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

function StreamingButton({ href, color, icon, label, testId }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:brightness-110 shadow-md"
      style={{ background: color }}
    >
      {icon}
      {label}
    </a>
  );
}

function AudioPreview({ previewUrl }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <div className="flex items-center gap-3 bg-zinc-900 rounded-xl px-4 py-3 border border-white/5">
      <audio ref={audioRef} src={previewUrl}
        onTimeUpdate={() => setProgress((audioRef.current?.currentTime / audioRef.current?.duration) * 100 || 0)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
      />
      <button onClick={toggle} data-testid="preview-play-btn"
        className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-rose-600 hover:bg-rose-700 transition-colors">
        {playing ? <Pause size={14} fill="white" className="text-white" /> : <Play size={14} fill="white" className="text-white ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-400 text-xs mb-1.5">30-Sek. Vorschau</p>
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-rose-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}

function SpotifyEmbed({ src, spotifyUrl }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <a href={spotifyUrl} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition-colors py-1">
        <Music2 size={14} /> Auf Spotify anhören <ExternalLink size={12} className="opacity-60" />
      </a>
    );
  }
  return (
    <iframe src={src} width="100%" height="80" frameBorder="0"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy" className="rounded-xl" title="Spotify Player"
      onError={() => setErrored(true)} />
  );
}

export default function ReleaseModal({ release: releaseProp, onClose }) {
  const [release, setRelease] = useState(releaseProp);
  const [loadingFull, setLoadingFull] = useState(false);

  // Auto-fetch vollständige Release-Daten wenn nur Teilinfos vorhanden (Hype, Charts, LatestReleases etc.)
  useEffect(() => {
    if (!releaseProp?.id) return;
    const hasFullData = releaseProp.beatport !== undefined
      || releaseProp.apple_music !== undefined
      || releaseProp.tracklist !== undefined
      || releaseProp.label !== undefined;
    if (hasFullData) {
      setRelease(releaseProp);
      return;
    }
    setLoadingFull(true);
    api.get(`/releases/by-id/${releaseProp.id}`)
      .then(res => setRelease(res.data))
      .catch(() => setRelease(releaseProp))
      .finally(() => setLoadingFull(false));
  }, [releaseProp?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [affiliateRules, setAffiliateRules] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [commentCount, setCommentCount] = useState(null);
  const { interactionCache } = usePublicAuth();
  const navigate = useNavigate();

  // hypeCount immer aus dem globalen Cache lesen (wird von HypeButton aktuell gehalten)
  const hypeCount = interactionCache[release?.id]?.hypeCount ?? (release?.hype_count || 0);

  useEffect(() => {
    api.get("/affiliate/public").then(r => setAffiliateRules(r.data || [])).catch(() => {});
  }, []);

  // Kommentar-Anzahl laden
  useEffect(() => {
    if (!release.id) return;
    api.get(`/comments/${release.id}/count`)
      .then(r => setCommentCount(r.data.count ?? 0))
      .catch(() => setCommentCount(0));
  }, [release.id]);

  useEffect(() => {
    if (!release.id) return;
    api.get(`/hype/status/${release.id}`)
      .then(r => {})  // HypeButton übernimmt das; Cache wird von HypeButton gepflegt
      .catch(() => {});
  }, [release.id]);

  useEffect(() => {
    if (!release.spotify && !release.spotify_preview_track) return;
    const previewSource = release.spotify_preview_track || release.spotify;
    api.get(`/spotify/preview?url=${encodeURIComponent(previewSource)}`)
      .then(r => { if (r.data?.preview_url) setPreviewUrl(r.data.preview_url); })
      .catch(() => {});
  }, [release.spotify, release.spotify_preview_track]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", handler); };
  }, [onClose]);

  const handleShare = async () => {
    if (release.post_name) {
      const url = `${window.location.origin}/release/${release.post_name}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareUrl(url); setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch { /* ignore */ }
    } else {
      try {
        const res = await api.post("/shortlinks/", { release_id: release.id });
        setShareUrl(res.data.share_url);
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(res.data.share_url);
          setCopied(true); setTimeout(() => setCopied(false), 3000);
        }
      } catch { /* silent */ }
    }
  };

  const af = url => applyAffiliateLink(url, affiliateRules);
  const spotifyEmbed = getSpotifyEmbedUrl(release.spotify_preview_track || release.spotify);
  const hasTracks = release.tracklist?.length > 0;
  const hasLinks = release.beatport || release.spotify || release.amazon || release.apple_music || release.deezer || release.youtube;
  const genres = release.categories?.filter(c => c !== release.typ).slice(0, 4) || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
      data-testid="release-modal"
    >
      <div
        className="relative bg-zinc-950 border border-white/8 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl flex flex-col shadow-2xl h-[calc(100dvh-env(safe-area-inset-top,20px)-16px)] sm:h-auto sm:max-h-[88vh]"
        style={{ animation: "slideUp 0.28s cubic-bezier(0.32,0.72,0,1)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Loading-Indikator beim Nachladen der vollen Daten */}
        {loadingFull && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 bg-zinc-900/80 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg">
            <svg className="w-3 h-3 animate-spin text-rose-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <span className="text-zinc-400 text-xs">Lädt…</span>
          </div>
        )}

        {/* Drag indicator – mobile only */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* ── HEADER: Cover + Info (nicht scrollend) ── */}
        <div className="relative flex-shrink-0">
          {/* Blurred bg from cover */}
          {release.cover_url && (
            <div className="absolute inset-0 overflow-hidden rounded-t-3xl sm:rounded-t-2xl">
              <img src={release.cover_url} alt="" className="w-full h-full object-cover scale-110 blur-2xl opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 to-zinc-950" />
            </div>
          )}

          {/* Close */}
          <button onClick={onClose} data-testid="modal-close-btn"
            className="absolute top-3 right-4 z-20 w-8 h-8 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-sm">
            <X size={15} />
          </button>

          <div className="relative z-10 flex items-start gap-5 p-5 pb-4">
            {/* Cover */}
            <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-xl overflow-hidden flex-shrink-0 shadow-xl ring-1 ring-white/10">
              <CoverImage
                cover_url={release.cover_url}
                interpret={release.interpret}
                titel={release.titel}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {release.typ && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-900/40 text-rose-300 border border-rose-700/30">
                    {release.typ}
                  </span>
                )}
                {release.erscheinungsdatum && (
                  <span className="text-zinc-500 text-xs">{formatDate(release.erscheinungsdatum)}</span>
                )}
              </div>
              <ArtistLinks
                release={release}
                className="block text-white font-black text-xl sm:text-2xl font-heading leading-tight hover:opacity-80 transition-opacity"
              />
              <p className="text-zinc-400 text-sm sm:text-base mt-0.5 leading-snug">{release.titel}</p>
              {release.label && (
                <Link
                  to={release.label_slug ? `/label/${release.label_slug}` : `/labels?search=${encodeURIComponent(release.label)}`}
                  data-testid="modal-label-link"
                  onClick={() => { if (onClose) onClose(); }}
                  className="inline-flex items-center gap-1 text-zinc-500 text-xs mt-1.5 hover:text-zinc-300 transition-colors">
                  <Disc3 size={10} />{release.label}
                </Link>
              )}
              {genres.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {genres.map(g => (
                    <span key={g} className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: "rgba(225,29,72,0.1)", color: "#fb7185" }}>
                      {g}
                    </span>
                  ))}
                </div>
              )}
              {/* Vote + Hype */}
              {release.id && (
                <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                  {/* Votes + Hype-count + Favorit row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <VoteButton release={release} size={18} showCount className="text-sm" />
                    <FavoriteButton releaseId={release.id} size={16} showLabel />
                    {hypeCount > 0 && (
                      <div data-testid="modal-hype-badge"
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                        style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.25)" }}>
                        <Zap size={11} className="text-amber-400" fill="currentColor" />
                        <span className="text-amber-300 text-xs font-black uppercase tracking-wide">HYPE</span>
                        <span className="text-amber-400 text-xs font-bold ml-0.5">{hypeCount}</span>
                      </div>
                    )}
                  </div>
                  {/* Hype CTA – volle Breite für Supporter / Upsell für andere */}
                  <HypeButton
                    releaseId={release.id}
                    size={14}
                    showCount={false}
                    showLabel
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── CONTENT (scrollend) ── */}
        <div className="overflow-y-auto flex-1 px-5 space-y-5 modal-scroll" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 16px) + 24px)" }}>

          {/* Hören & Kaufen */}
          {hasLinks && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-3">Hören &amp; Kaufen</p>
              <div className="flex flex-wrap gap-2">
                <StreamingButton href={af(release.beatport)} color="linear-gradient(135deg,#FF6F00,#E05E00)" icon={<ShoppingCart size={13} />} label="Beatport" testId="beatport-link" />
                <StreamingButton href={af(release.spotify)} color="linear-gradient(135deg,#1DB954,#158a3e)" icon={<Music2 size={13} />} label="Spotify" testId="spotify-link" />
                <StreamingButton href={af(release.amazon)} color="linear-gradient(135deg,#FF9900,#cc7a00)" icon={<ShoppingCart size={13} />} label="Amazon" testId="amazon-link" />
                <StreamingButton href={af(release.apple_music)} color="linear-gradient(135deg,#fc3c44,#c4162a)" icon={<Apple size={13} />} label="Apple Music" testId="apple-music-link" />
                <StreamingButton href={af(release.deezer)} color="linear-gradient(135deg,#a238ff,#7000e0)" icon={<Headphones size={13} />} label="Deezer" testId="deezer-link" />
                <StreamingButton href={release.youtube} color="linear-gradient(135deg,#FF0000,#cc0000)" icon={<Youtube size={13} />} label="YouTube" testId="youtube-link" />
              </div>
            </div>
          )}

          {/* Action row: Share + Detail-Link + Kommentare */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleShare} data-testid="share-btn"
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: copied ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.06)", color: copied ? "#86efac" : "#a1a1aa", border: `1px solid ${copied ? "rgba(34,197,94,0.25)" : "rgba(255,255,255,0.08)"}` }}>
              {copied ? <Check size={13} /> : <Share2 size={13} />}
              {copied ? "Link kopiert!" : "Teilen"}
            </button>
            {release.post_name && (
              <>
                <button
                  onClick={() => { onClose(); navigate(`/release/${release.post_name}`); }}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-zinc-500 hover:text-white transition-colors"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <ArrowUpRight size={13} /> Zur Release-Seite
                </button>
                <button
                  onClick={() => { onClose(); navigate(`/release/${release.post_name}#comments`); }}
                  data-testid="modal-comments-link"
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold text-zinc-500 hover:text-rose-400 transition-colors"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <MessageSquare size={13} />
                  Kommentare
                  {commentCount !== null && commentCount > 0 && (
                    <span className="ml-1 text-xs font-bold text-zinc-600 bg-zinc-800 rounded-full px-1.5 py-0.5 leading-none">
                      {commentCount}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>

          {shareUrl && (
            <div className="flex items-center gap-2 bg-zinc-900 rounded-lg px-3 py-2 text-xs text-zinc-500 border border-white/5">
              <Copy size={10} className="flex-shrink-0" />
              <span className="truncate font-mono">{shareUrl}</span>
            </div>
          )}

          {/* Vorschau */}
          {(previewUrl || spotifyEmbed) && (
            <>
              <div className="h-px bg-white/5" />
              <div data-testid={previewUrl ? "audio-preview-section" : "spotify-embed"}>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-3">Vorschau</p>
                {previewUrl
                  ? <AudioPreview previewUrl={previewUrl} />
                  : <SpotifyEmbed src={spotifyEmbed} spotifyUrl={release.spotify} />
                }
              </div>
            </>
          )}

          {/* Tracklist */}
          {hasTracks && (
            <>
              <div className="h-px bg-white/5" />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-600 mb-3">
                  Tracklist
                  <span className="ml-2 text-zinc-700 normal-case tracking-normal font-normal">
                    {release.tracklist.length} {release.tracklist.length === 1 ? "Track" : "Tracks"}
                  </span>
                </p>
                <ol className="space-y-0">
                  {release.tracklist.map((track, i) => (
                    <li key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-white/5 transition-colors group">
                      <span className="text-zinc-700 text-xs font-mono w-5 text-right flex-shrink-0 group-hover:text-zinc-500 transition-colors">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-zinc-300 group-hover:text-white text-sm transition-colors flex-1">{track}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(60px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
